import './App.scss'
import './assets/images/favicon.png'

import * as Sentry from '@sentry/react'

import {
	Agenda,
	AgendaDetail,
	CollaboratorDetail,
	Collaborators,
	Cookies,
	Donation,
	DonationThanks,
	Edition,
	EditionRegistration,
	Error404,
	Event,
	Events,
	Faq,
	Fullpage,
	Home,
	News,
	NewsDetail,
	NewsletterPage,
	ProjectDetail,
	Sidebarpage,
	TeamDetail,
	Teams,
	Trips
} from './views/pages/index'
import React, {useRef} from 'react'
import {Route, Router} from 'react-router'
import {ThemeProvider, createMuiTheme} from '@material-ui/core'

import {CartProvider} from './util/cart'
import {Comingsoon} from './views/webshop/pages/index'
import {CookiesProvider} from './cookies/cookies'
import {Fetcher} from '@codeurs/front'
import {LanguageProvider} from './util/i18n'
import {PageData} from 'types'
import ReactDOM from 'react-dom'
import {Rrn} from './views/pages/Rrn'
import {ScrollMemory} from './util/scrollmemory'
import {createBrowserHistory} from 'history'

if (process.env.SENTRY_DSN)
	Sentry.init({
		dsn: process.env.SENTRY_DSN,
		release: process.env.PROJECT_RELEASE,
		environment: process.env.NODE_ENV,
		allowUrls: [/https?:\/\/((scholen|webshop|www)\.)?broederlijkdelen\.be/],
		ignoreErrors: [
			// Random plugins/extensions
			'top.GLOBALS',
			// See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
			'originalCreateNotification',
			'canvas.contentDocument',
			'MyApp_RemoveAllHighlights',
			'http://tt.epicplay.com',
			"Can't find variable: ZiteReader",
			'jigsaw is not defined',
			'ComboSearch is not defined',
			'http://loading.retry.widdit.com/',
			'atomicFindClose',
			// Facebook borked
			'fb_xd_fragment',
			// ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
			// reduce this. (thanks @acdha)
			// See http://stackoverflow.com/questions/4113268
			'bmi_SafeAddOnload',
			'EBCallBackMessageReceived',
			// See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
			'conduitPage',
			// Outlook Safe Link scanning
			// See https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/13
			'Non-Error exception captured',
			'Non-Error promise rejection captured'
		],
		denyUrls: [
			// Facebook flakiness
			/graph\.facebook\.com/i,
			// Facebook blocked
			/connect\.facebook\.net\/en_US\/all\.js/i,
			// Woopra flakiness
			/eatdifferent\.com\.woopra-ns\.com/i,
			/static\.woopra\.com\/js\/woopra\.js/i,
			// Chrome extensions
			/extensions\//i,
			/^chrome:\/\//i,
			// Other plugins
			/127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
			/webappstoolbarba\.texthelp\.com\//i,
			/metrics\.itunes\.apple\.com\.edgesuite\.net\//i
		]
	})

const webshopPages = {
	webshop: Comingsoon, //Webshop,
	products: Comingsoon, //WebshopProducts,
	product_detail_downloadable: Comingsoon, //WebshopProduct,
	product_detail_physical: Comingsoon, //WebshopProduct,
	basket: Comingsoon, //Cart,
	basket_confirmation: Comingsoon, //Fullpage,
	downloads: Comingsoon, //Downloads,
	faq: Faq,
	fullpage: Fullpage,
	sidebarpage: Sidebarpage,
	cookies: Cookies,
	error404: Error404
}
const pages = {
	home: Home,
	faq: Faq,
	fullpage: Fullpage,
	sidebarpage: Sidebarpage,
	news: News,
	news_detail: NewsDetail,
	collaborators: Collaborators,
	collaborator_detail: CollaboratorDetail,
	stories: Fullpage,
	story_detail: Sidebarpage,
	agenda: Agenda,
	agenda_detail: AgendaDetail,
	regions: Fullpage,
	region_detail: Sidebarpage,
	themes: Fullpage,
	theme_detail: Sidebarpage,
	projects: Fullpage,
	project_detail: ProjectDetail,
	jobs: Fullpage,
	job_detail: Sidebarpage,
	events: Events,
	event: Event,
	edition: Edition,
	edition_registration: EditionRegistration,
	teams: Teams,
	team_detail: TeamDetail,
	team_member: TeamDetail,
	trips: Trips,
	trip_detail: Sidebarpage,
	donation: Donation,
	donation_thanks: DonationThanks,
	rrn: Rrn,
	newsletter: NewsletterPage,
	cookies: Cookies,
	error404: Error404
}

const initial = window['__INITIAL_STATE__'] || {}

const history = createBrowserHistory()

const MuiTheme = createMuiTheme({
	typography: {
		fontFamily: 'inherit'
	},
	palette: {
		primary: {
			main: '#61c250'
		},
		secondary: {
			main: '#61c250'
		}
	}
})

export const App = () => {
	const currentLocation = useRef()

	return (
		<Router history={history}>
			<Route>
				{({location}) => {
					const {pathname, search} = location
					return (
						<Fetcher<PageData>
							url={`${pathname.replace(/\/$/, '')}${search}${
								search ? '&' : '?'
							}json`}
							hydrate={initial}
						>
							{({isLoading, data, error}) => {
								const View = getViewByProjectKey(data)
								const {
									channel,
									key: pageKey,
									cookies,
									copy,
									translatedLanguage,
									...entry
								} = data
								const language = translatedLanguage || data.language
								const props = {...entry, pageKey, channel, isLoading, error}
								const view = View.prototype.render ? (
									<View {...props} />
								) : (
									View(props)
								)
								if (pathname === (data.url || '/')) {
									currentLocation.current = location
									document.title =
										(data.metadata && data.metadata.title) || data.title
								}
								return (
									<LanguageProvider language={language} copy={copy}>
										<CookiesProvider config={cookies} page={data}>
											<ThemeProvider theme={MuiTheme}>
												<CartProvider storageKey="cart">
													<ScrollMemory
														history={history}
														location={currentLocation.current || location}
													/>
													{view}
												</CartProvider>
											</ThemeProvider>
										</CookiesProvider>
									</LanguageProvider>
								)
							}}
						</Fetcher>
					)
				}}
			</Route>
		</Router>
	)
}

const getViewByProjectKey = (data: PageData) => {
	switch (data.project_key) {
		case 'webshop':
			return findPageOr404(data, webshopPages)
		case 'scholen':
		case 'broederlijkdelen':
		default:
			return findPageOr404(data, pages)
	}
}

const findPageOr404 = (data: PageData, projectPagesKey) => {
	const {channel} = data
	return projectPagesKey[channel] || Error404
}

ReactDOM.render(<App />, document.getElementById('root'))
